import React, { useState } from "react";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Button } from "../../../components/ui/button";
import {
  User,
  FileCheck,
  PenTool,
  Landmark,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import OnboardingFormShell, {
  OnboardingFormStep,
} from "../../../components/onboarding/OnboardingFormShell";
import SignatureDialog, {
  SignatureType,
} from "../../../components/onboarding/SignatureDialog";
import { useOnboardingForm } from "../../../hooks/useOnboardingForm";
import { toast } from "sonner";

interface TfnDeclarationData {
  // Your Details
  taxFileNumber: string;
  noTfnYet: boolean;
  title: string;
  legalFirstName: string;
  legalLastName: string;
  homeAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  dateOfBirth: string;
  // Employment & Tax Options
  basisOfPayment: string;
  isAustralianResident: string;
  claimTaxFreeThreshold: string;
  hasHelpDebt: boolean;
  hasSslDebt: boolean;
  hasFinancialSupplement: boolean;
  hasNoStudyLoan: boolean;
  // Declaration & Signature
  fullName: string;
  signatureDate: string;
  signatureType: string;
  signatureData: string;
  agreed: boolean;
}

const STEPS: OnboardingFormStep[] = [
  { id: 1, label: "Your Details", icon: User },
  { id: 2, label: "Employment & Tax", icon: Landmark },
  { id: 3, label: "Declaration & Signature", icon: PenTool },
];

const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Other"];

const STATE_OPTIONS = [
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "QLD", label: "Queensland" },
  { value: "WA", label: "Western Australia" },
  { value: "SA", label: "South Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "ACT", label: "Australian Capital Territory" },
  { value: "NT", label: "Northern Territory" },
];

const BASIS_OF_PAYMENT_OPTIONS = [
  { value: "full-time", label: "Full-time employment" },
  { value: "part-time", label: "Part-time employment" },
  { value: "casual", label: "Casual employment" },
  { value: "labour-hire", label: "Labour hire" },
  { value: "superannuation", label: "Superannuation pension or annuity" },
];

const initialData: TfnDeclarationData = {
  taxFileNumber: "",
  noTfnYet: false,
  title: "",
  legalFirstName: "",
  legalLastName: "",
  homeAddress: "",
  suburb: "",
  state: "",
  postcode: "",
  dateOfBirth: "",
  basisOfPayment: "",
  isAustralianResident: "",
  claimTaxFreeThreshold: "",
  hasHelpDebt: false,
  hasSslDebt: false,
  hasFinancialSupplement: false,
  hasNoStudyLoan: false,
  fullName: "",
  signatureDate: new Date().toISOString().split("T")[0],
  signatureType: "Draw",
  signatureData: "",
  agreed: false,
};

interface TfnDeclarationFormProps {
  /** HR preview mode: renders the hire's submitted form read-only */
  previewRequestId?: string | null;
}

const TfnDeclarationForm: React.FC<TfnDeclarationFormProps> = ({ previewRequestId }) => {
  const {
    formData,
    updateField,
    currentStep,
    setCurrentStep,
    loading,
    saving,
    isCompleted,
    isEditing,
    submittedAt,
    startEditing,
    saveProgress,
    submit,
    isPreview,
  } = useOnboardingForm<TfnDeclarationData>("tfn-declaration", initialData, {
    previewRequestId,
  });

  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);

  const readOnly = isPreview || (isCompleted && !isEditing);

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return (
          (formData.noTfnYet || !!formData.taxFileNumber.trim()) &&
          !!formData.title &&
          !!formData.legalFirstName.trim() &&
          !!formData.legalLastName.trim() &&
          !!formData.homeAddress.trim() &&
          !!formData.suburb.trim() &&
          !!formData.state &&
          !!formData.postcode.trim() &&
          !!formData.dateOfBirth
        );
      case 2:
        return (
          !!formData.basisOfPayment &&
          !!formData.isAustralianResident &&
          !!formData.claimTaxFreeThreshold &&
          (formData.hasHelpDebt ||
            formData.hasSslDebt ||
            formData.hasFinancialSupplement ||
            formData.hasNoStudyLoan)
        );
      case 3:
        return (
          !!formData.fullName.trim() &&
          !!formData.signatureDate &&
          !!formData.signatureData &&
          formData.agreed
        );
      default:
        return true;
    }
  };

  const handleStepChange = (step: number) => {
    if (step > currentStep && !isStepValid(currentStep)) {
      toast.error("Please fill in all required fields before continuing");
      return;
    }
    setCurrentStep(step);
  };

  const canSubmit = isStepValid(1) && isStepValid(2) && isStepValid(3);

  const inputClass = "h-11 rounded-xl border-gray-200 bg-gray-50";
  const labelClass = "text-sm font-medium text-gray-900";

  // ─── STEP 1: Your Details ───
  const renderYourDetails = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Your Details</h3>
        <p className="text-sm text-gray-500">
          As shown on your ATO records. This form follows the ATO Tax file
          number declaration (NAT 3092).
        </p>
      </div>

      <div className="space-y-2">
        <label className={labelClass}>
          Tax File Number (TFN) <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder="e.g. 123 456 789"
          value={formData.taxFileNumber}
          onChange={(e) => updateField("taxFileNumber", e.target.value)}
          disabled={readOnly || formData.noTfnYet}
          className={inputClass}
          maxLength={11}
        />
        <label className="flex items-center gap-2 cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={formData.noTfnYet}
            onChange={(e) => {
              updateField("noTfnYet", e.target.checked);
              if (e.target.checked) updateField("taxFileNumber", "");
            }}
            disabled={readOnly}
            className="w-4 h-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-600">
            I don't have a TFN yet — I have applied (or will apply) for one
            with the ATO
          </span>
        </label>
        {formData.noTfnYet && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              You must provide your TFN to payroll within 28 days or tax will be
              withheld at the top marginal rate.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>
            Title <span className="text-red-500">*</span>
          </label>
          <Select
            value={formData.title}
            onValueChange={(v) => updateField("title", v)}
            disabled={readOnly}
          >
            <SelectTrigger className={inputClass}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {TITLE_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Legal First Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="First name"
            value={formData.legalFirstName}
            onChange={(e) => updateField("legalFirstName", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Legal Last Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Last name"
            value={formData.legalLastName}
            onChange={(e) => updateField("legalLastName", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <label className={labelClass}>
            Home Address <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Street address"
            value={formData.homeAddress}
            onChange={(e) => updateField("homeAddress", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Suburb <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Suburb"
            value={formData.suburb}
            onChange={(e) => updateField("suburb", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className={labelClass}>
              State <span className="text-red-500">*</span>
            </label>
            <Select
              value={formData.state}
              onValueChange={(v) => updateField("state", v)}
              disabled={readOnly}
            >
              <SelectTrigger className={inputClass}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {STATE_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className={labelClass}>
              Postcode <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g. 2000"
              value={formData.postcode}
              onChange={(e) => updateField("postcode", e.target.value)}
              disabled={readOnly}
              className={inputClass}
              maxLength={4}
            />
          </div>
        </div>
      </div>

      <div className="space-y-2 md:max-w-xs">
        <label className={labelClass}>
          Date of Birth <span className="text-red-500">*</span>
        </label>
        <Input
          type="date"
          value={formData.dateOfBirth}
          onChange={(e) => updateField("dateOfBirth", e.target.value)}
          disabled={readOnly}
          className={inputClass}
        />
      </div>
    </div>
  );

  // ─── STEP 2: Employment & Tax Options ───
  const renderEmploymentTax = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Employment & Tax Options</h3>
        <p className="text-sm text-gray-500">
          Used by payroll to work out how much tax to withhold from your pay.
        </p>
      </div>

      <div className="space-y-3">
        <label className={labelClass}>
          What is your basis of payment? <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {BASIS_OF_PAYMENT_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                formData.basisOfPayment === option.value
                  ? "bg-blue-50/50 border-blue-200"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              } ${readOnly ? "pointer-events-none" : ""}`}
            >
              <input
                type="radio"
                name="basisOfPayment"
                checked={formData.basisOfPayment === option.value}
                onChange={() => updateField("basisOfPayment", option.value)}
                disabled={readOnly}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-900">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <label className={labelClass}>
          Are you an Australian resident for tax purposes?{" "}
          <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500">
          Generally you are an Australian resident if you live permanently in
          Australia. See ato.gov.au/residency if unsure.
        </p>
        <div className="flex gap-3">
          {["yes", "no"].map((value) => (
            <label
              key={value}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                formData.isAustralianResident === value
                  ? "bg-blue-50/50 border-blue-200"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              } ${readOnly ? "pointer-events-none" : ""}`}
            >
              <input
                type="radio"
                name="isAustralianResident"
                checked={formData.isAustralianResident === value}
                onChange={() => updateField("isAustralianResident", value)}
                disabled={readOnly}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-900 capitalize">{value}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <label className={labelClass}>
          Do you want to claim the tax-free threshold from this payer?{" "}
          <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500">
          The tax-free threshold is ${"18,200"} per year. Only claim it from
          one payer at a time — usually your main source of income.
        </p>
        <div className="flex gap-3">
          {["yes", "no"].map((value) => (
            <label
              key={value}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                formData.claimTaxFreeThreshold === value
                  ? "bg-blue-50/50 border-blue-200"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              } ${readOnly ? "pointer-events-none" : ""}`}
            >
              <input
                type="radio"
                name="claimTaxFreeThreshold"
                checked={formData.claimTaxFreeThreshold === value}
                onChange={() => updateField("claimTaxFreeThreshold", value)}
                disabled={readOnly}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-900 capitalize">{value}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <label className={labelClass}>
          Do you have a government study or training loan?{" "}
          <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500">
          Tick any that apply — payroll will withhold extra for compulsory
          repayments.
        </p>
        <div className="space-y-2">
          {[
            { field: "hasHelpDebt" as const, label: "Higher Education Loan Program (HELP / HECS-HELP)" },
            { field: "hasSslDebt" as const, label: "Student Start-up Loan (SSL) or Trade Support Loan (TSL)" },
            { field: "hasFinancialSupplement" as const, label: "Financial Supplement loan (SFSS)" },
          ].map(({ field, label }) => (
            <label key={field} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData[field]}
                onChange={(e) => {
                  updateField(field, e.target.checked);
                  if (e.target.checked) updateField("hasNoStudyLoan", false);
                }}
                disabled={readOnly}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.hasNoStudyLoan}
              onChange={(e) => {
                updateField("hasNoStudyLoan", e.target.checked);
                if (e.target.checked) {
                  updateField("hasHelpDebt", false);
                  updateField("hasSslDebt", false);
                  updateField("hasFinancialSupplement", false);
                }
              }}
              disabled={readOnly}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">
              No — I don't have a government study or training loan
            </span>
          </label>
        </div>
      </div>
    </div>
  );

  // ─── STEP 3: Declaration & Signature ───
  const renderDeclaration = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Declaration & Signature</h3>
        <p className="text-sm text-gray-500">Read the declaration carefully before signing</p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <FileCheck className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700 space-y-2">
            <p className="font-medium text-gray-900">I declare that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>The information I have given on this form is true and correct.</li>
              <li>I understand there are penalties for deliberately making a false or misleading statement.</li>
              <li>
                If I have not provided my TFN, I will provide it to my payer
                within 28 days of this declaration.
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>
            Full Legal Name for Signature <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Your full legal name"
            value={formData.fullName}
            onChange={(e) => updateField("fullName", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Date <span className="text-red-500">*</span>
          </label>
          <Input
            type="date"
            value={formData.signatureDate}
            onChange={(e) => updateField("signatureDate", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>

      {/* Digital Signature */}
      <div className="space-y-2">
        <label className={labelClass}>
          Digital Signature <span className="text-red-500">*</span>
        </label>
        {formData.signatureData ? (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3">
              {formData.signatureType === "Draw" ? (
                <img src={formData.signatureData} alt="Signature" className="max-h-10" />
              ) : (
                <span className="font-signature text-2xl">{formData.signatureData}</span>
              )}
            </div>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateField("signatureData", "")}
                className="rounded-lg"
              >
                Clear
              </Button>
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsSignatureDialogOpen(true)}
            disabled={readOnly}
            className="w-full rounded-xl border-dashed border-gray-300 py-6 gap-2"
          >
            <PenTool className="w-4 h-4" />
            Click to Sign
          </Button>
        )}
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.agreed}
          onChange={(e) => updateField("agreed", e.target.checked)}
          disabled={readOnly}
          className="w-4 h-4 rounded border-gray-300 mt-0.5"
        />
        <span className="text-sm text-gray-700">
          I agree to the declaration above and confirm this electronic signature
          is legally binding.
        </span>
      </label>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderYourDetails();
      case 2:
        return renderEmploymentTax();
      case 3:
        return renderDeclaration();
      default:
        return renderYourDetails();
    }
  };

  return (
    <>
      <OnboardingFormShell
        title="TFN Declaration"
        steps={STEPS}
        currentStep={currentStep}
        onStepChange={handleStepChange}
        onSaveProgress={saveProgress}
        onSubmit={submit}
        saving={saving}
        loading={loading}
        submitDisabled={!canSubmit}
        readOnly={readOnly}
        submittedAt={submittedAt}
        onEdit={isPreview ? undefined : startEditing}
        backTo={previewRequestId ? `/my-onboarding?requestId=${previewRequestId}` : undefined}
        isResubmission={isCompleted}
      >
        {renderStepContent()}
      </OnboardingFormShell>

      <SignatureDialog
        open={isSignatureDialogOpen}
        onOpenChange={setIsSignatureDialogOpen}
        onConfirm={(type: SignatureType, data: string) => {
          updateField("signatureType", type);
          updateField("signatureData", data);
        }}
      />
    </>
  );
};

export default TfnDeclarationForm;
